import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import { useChatStore } from "@/stores/chat-store";
import { HELP_IDS } from "@/lib/help-ids";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Keyboard shortcut hook for Help Center.
 * - `?` opens the help center menu (ignored when focus is in a form field)
 * - `Escape` closes the help center when it's open
 */
function useHelpShortcut() {
  const openHelpCenter = useChatStore((s) => s.openHelpCenter);
  const closeHelpCenter = useChatStore((s) => s.closeHelpCenter);
  const isHelpCenterOpen = useChatStore((s) => s.isHelpCenterOpen);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const isEditable = (e.target as HTMLElement).isContentEditable;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || isEditable) return;

      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        openHelpCenter("menu");
      }
      if (e.key === "Escape" && isHelpCenterOpen) {
        e.preventDefault();
        closeHelpCenter();
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [openHelpCenter, closeHelpCenter, isHelpCenterOpen]);
}

/**
 * Help button for the bottom dock.
 * Opens the Help Center modal and registers the `?` / `Escape` keyboard shortcuts.
 */
export function HelpButton() {
  const openHelpCenter = useChatStore((s) => s.openHelpCenter);
  const showHelpTooltip = useChatStore((s) => s.showHelpTooltip);
  const onboardingProgress = useChatStore((s) => s.onboardingProgress);
  const setShowHelpTooltip = useChatStore((s) => s.setShowHelpTooltip);
  const updateOnboardingProgress = useChatStore((s) => s.updateOnboardingProgress);

  const showTooltip =
    showHelpTooltip &&
    !onboardingProgress.hasSeenHelpTooltip &&
    !onboardingProgress.hasCompletedWizard;

  const handleTooltipDismiss = useCallback(() => {
    setShowHelpTooltip(false);
    updateOnboardingProgress({ hasSeenHelpTooltip: true });
  }, [setShowHelpTooltip, updateOnboardingProgress]);

  useHelpShortcut();

  useEffect(() => {
    if (!showTooltip) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleTooltipDismiss();
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [handleTooltipDismiss, showTooltip]);

  return (
    <Tooltip>
      <div className="relative flex items-center justify-center">
        <AnimatePresence>
          {showTooltip ? (
            <motion.button
              key="help-tooltip-pill"
              type="button"
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={handleTooltipDismiss}
              className="absolute bottom-full left-1/2 z-20 mb-2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-rose-500 px-3 py-1.5 text-xs font-medium text-white shadow-lg shadow-rose-950/25 ring-1 ring-rose-300/40 pointer-events-auto cursor-pointer"
              aria-label="Dismiss Help Center tooltip"
            >
              <HelpCircle className="h-3.5 w-3.5" />
              <span>Help Center ?</span>
            </motion.button>
          ) : null}
        </AnimatePresence>

        <TooltipTrigger asChild>
          <button
            onClick={() => {
              openHelpCenter("menu");
            }}
            aria-label="Help Center (?)"
            data-help-id={HELP_IDS.dock.help}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground transition-all hover:bg-white/[0.06] hover:text-foreground"
          >
            <HelpCircle className="h-[18px] w-[18px]" />
          </button>
        </TooltipTrigger>
      </div>
      <TooltipContent side="top" className="text-xs">
        Help Center <kbd className="ml-1 rounded bg-muted px-1 py-0.5 text-[10px]">?</kbd>
      </TooltipContent>
    </Tooltip>
  );
}
